import { financialYearLabel, financialYearOf, type FyStartMonth } from '@/lib/financialYear'
import type { LoanEntry } from '@/api/loans'

export type LoanEntryWithOutstanding = LoanEntry & { outstandingAfter: number }

export interface LoanYearGroup {
  /** The financial year the entries fall in (#390). */
  year: number
  label: string
  entries: LoanEntryWithOutstanding[]
  /** Principal repaid (EMIs and prepayments); increases are not repayments. */
  principal: number
  interest: number
}

// Summing rupee amounts as JS numbers drifts by fractions of a paisa; snap back to paise.
const paise = (n: number) => Math.round(n * 100) / 100

/** Entries arrive newest first; replays them backwards from today's outstanding. */
export function withOutstandingAfter(
  entries: LoanEntry[],
  outstanding: number
): LoanEntryWithOutstanding[] {
  let after = outstanding
  return entries.map((e) => {
    const row = { ...e, outstandingAfter: after }
    after = paise(e.type === 'INCREASE' ? after - e.principalAmount : after + e.principalAmount)
    return row
  })
}

export function emiRecordedInMonth(entries: LoanEntry[], month: Date): boolean {
  const prefix = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}`
  return entries.some((e) => e.type === 'EMI' && e.date.startsWith(prefix))
}

export function groupByYear(
  entries: LoanEntryWithOutstanding[],
  fy: FyStartMonth
): LoanYearGroup[] {
  const groups: LoanYearGroup[] = []
  for (const e of entries) {
    const year = financialYearOf(e.date, fy)
    let group = groups.find((g) => g.year === year)
    if (!group) {
      group = { year, label: financialYearLabel(year, fy), entries: [], principal: 0, interest: 0 }
      groups.push(group)
    }
    group.entries.push(e)
    if (e.type !== 'INCREASE') group.principal = paise(group.principal + e.principalAmount)
    group.interest = paise(group.interest + e.interestAmount)
  }
  return groups
}
